import { useCurrentUser } from "@/api/current-user";
import { indexedQuery } from "@/api/query";
import { useUsers } from "@/api/user-query";
import { useDataCatalog } from "@/composable/data-catalog";
import { useDataFormatter } from "@/composable/data-formatter";
import {
	AuditTarget,
	ConditionType,
	Role,
	ThresholdComparison,
} from "@soft-db/monitoring-api-client";
import { DateTime } from "luxon";
import { RouteLocationRaw } from "vue-router";
import { useSites } from "../../api/site-query";

export type AuditTargetLink = {
	text: string;
	location: RouteLocationRaw;
};

export function useAuditValueResolver() {
	const { t } = useI18n();
	const { secondsToPrettyString } = useDataFormatter();

	const weekdays = computed(() => [
		t("common.sun"),
		t("common.mon"),
		t("common.tue"),
		t("common.wed"),
		t("common.thu"),
		t("common.fri"),
		t("common.sat"),
	]);

	function parseWeekdaysBitfield(bitfield: number): string {
		return weekdays.value.filter((_, index) => (bitfield & (1 << index)) > 0).join(", ");
	}

	/**
	 * The list is a string in the following format
	 * "{0, 2, 4, 5}"
	 */
	function parseWeekdaysList(list: string): string {
		return (
			list
				// Remove curly brace from string
				.replaceAll(/{|}/g, "")
				.split(",")
				.map((idx) => weekdays.value[Number(idx)])
				.join(", ")
		);
	}

	const { data: currentUser } = useCurrentUser();

	function parseWeekMinutes(weekMinutes: number): string {
		const hour = Math.floor(weekMinutes / 60);
		const minute = weekMinutes % 60;

		return DateTime.now()
			.set({ hour, minute })
			.toLocaleString(DateTime.TIME_SIMPLE, { locale: currentUser.value?.preferredLang });
	}

	const statusDataTypes = computed(() => [
		t("status-data-types.slm"),
		t("status-data-types.vibAcc"),
		t("status-data-types.vibVel"),
	]);

	function parseStatusDataType(dataType: number): string {
		return statusDataTypes.value[dataType];
	}

	const { data: sites } = indexedQuery(useSites());
	// TODO: Fix api
	const { data: users } = indexedQuery(useUsers());
	const roles: Record<Role, string> = {
		sys_admin: t("role.sys_admin"),
		admin: t("role.admin"),
		editor: t("role.editor"),
		viewer: t("role.viewer"),
	};
	const local: Record<string, string> = {
		"en-CA": t("local.en-CA"),
		"en-US": t("local.en-US"),
		"fr-CA": t("local.fr-CA"),
	};

	const { Comparison } = useDataCatalog();

	const alertConditionModes = computed(() => ({
		and: t("alert-settings.andMode"),
		or: t("alert-settings.orMode"),
		always: t("alert-settings.alwaysMode"),
	}));

	function resolveTargetLocation(name: string, value: string): AuditTargetLink | null {
		let location: RouteLocationRaw;
		let textDisplay: string;

		switch (name) {
			case "site":
				location = { name: "sites-id-settings", params: { id: value } };
				textDisplay = sites.value?.get(value)?.name ?? value;
				break;

			default:
				return null;
		}

		return { location, text: textDisplay };
	}

	function resolveValueName(target: AuditTarget | "", name: string, value: string): string {
		if (target === "alert") {
			if (name === "start" || name === "end") {
				return parseWeekMinutes(Number(value));
			}

			if (name === "rateLimiter.mode") {
				return t("alert-settings.rateLimiterModes." + value);
			}

			if (name === "comparison") {
				const comparison = value as ThresholdComparison;
				switch (comparison) {
					case "gt":
						return t("common.greaterThan");
					case "lt":
						return t("common.lessThan");
					default:
						throw comparison satisfies never;
				}
			}

			if (name === "type") {
				const type = value as ConditionType;
				switch (type) {
					case "record":
						return t("alert-settings.intervalData");
					case "vibration_event":
						return t("alert-settings.vibrationEvent");
					case "weekly_limit":
						return t("common.weeklyLimit");
					default: // Exhaustive check
						throw type satisfies never;
				}
			}
		}

		if (target === "weekly_limit") {
			if (name === "from" || name === "to") {
				return parseWeekMinutes(Number(value));
			}
		}

		if (target === "safe_limit" || target === "vibration_criteria") {
			if (name === "x") {
				return `${value} Hz`;
			} else if (name === "y") {
				return `${value} m/s`;
			} else return value;
		}

		switch (name) {
			case "weekdays": {
				const maybeBitfield = Number(value);
				if (!isNaN(maybeBitfield)) {
					return parseWeekdaysBitfield(maybeBitfield);
				}

				return parseWeekdaysList(value);
			}

			// case "group":
			// 	return groups.value?.get(value)?.name ?? value;

			case "site":
			case "project":
				return sites.value?.get(value)?.name ?? value;

			case "subscribers":
			case "recipients.users":
				return users.value?.get(value)?.name ?? value;

			case "statusDataType":
				return parseStatusDataType(Number(value));

			case "period":
			case "definition.period":
			case "rateLimiter.period":
				return secondsToPrettyString(Number(value));

			case "definition.comparison":
				return Comparison[value] ?? value;

			case "conditionMode":
			case "validityMode":
				return alertConditionModes.value[value] ?? value;

			case "image.id":
				return t("common.notAvailable");

			case "identity.password":
				return t("audit.passwordChanged");
			case "identity.email":
				return t("audit.emailChanged");
			case "identity.publicToken":
				return value ? t("client-settings.isPublic") : t("client-settings.isPrivate");

			case "role":
				return roles[value] ?? value;
			case "preferredLang":
				return local[value] ?? value;

			case "archived":
				return value ? t("common.yes") : t("common.no");
		}

		if (value === "true" || value === "t") {
			return t("common.yes");
		}
		if (value === "false" || value === "f") {
			return t("common.no");
		}

		return value;
	}

	return { resolveValueName, resolveTargetLocation };
}
